import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Mail, Building, Phone, Calendar, CheckCircle, Clock } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem("brgt_token");
const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

export default function AdminContacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const fetchContacts = async () => {
    try {
      const { data } = await axios.get(`${API}/contact`, authHeaders());
      setContacts(data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchContacts(); }, []);

  const markAsRead = async (contact) => {
    try {
      await axios.put(`${API}/contact/${contact.id}/status?status=read`, {}, authHeaders());
      toast.success("Marked as read");
      fetchContacts();
    } catch {
      toast.error("Failed to update");
    }
  };

  return (
    <div data-testid="admin-contacts">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-bold text-navy" data-testid="contacts-title">Contact Submissions</h1>
        <p className="text-slate-500 text-sm mt-1">View and manage contact form submissions</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* List */}
        <div className="lg:col-span-5">
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3].map(i => <div key={i} className="bg-white rounded-xl h-20 animate-pulse border border-slate-100" />)}
            </div>
          ) : contacts.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-100 p-12 text-center">
              <p className="text-slate-400">No submissions yet</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">
              {contacts.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setSelected(c)}
                  data-testid={`contact-item-${c.id}`}
                  className={`w-full text-left rounded-xl border p-4 transition-all ${
                    selected?.id === c.id
                      ? "border-navy bg-navy/5"
                      : "border-slate-100 bg-white hover:border-slate-200"
                  }`}
                >
                  <div className="flex items-start justify-between mb-1">
                    <p className="text-sm font-semibold text-navy truncate">{c.name}</p>
                    <Badge className={`text-xs ml-2 flex-shrink-0 ${c.status === 'new' ? 'bg-amber/20 text-amber-dark' : 'bg-slate-100 text-slate-500'}`}>
                      {c.status === 'new' ? <Clock size={10} className="mr-1" /> : <CheckCircle size={10} className="mr-1" />}
                      {c.status}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-400 truncate">{c.email}</p>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2">{c.message}</p>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Detail */}
        <div className="lg:col-span-7">
          {selected ? (
            <div className="bg-white rounded-xl border border-slate-100 p-6" data-testid="contact-detail">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading font-semibold text-navy text-lg">{selected.name}</h2>
                {selected.status === "new" && (
                  <button
                    onClick={() => markAsRead(selected)}
                    data-testid="mark-read-btn"
                    className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 rounded-full px-4 py-1.5 text-xs font-semibold transition-colors"
                  >
                    <CheckCircle size={12} /> Mark as Read
                  </button>
                )}
              </div>
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-2 text-sm">
                  <Mail size={14} className="text-slate-400" />
                  <a href={`mailto:${selected.email}`} className="text-navy hover:underline">{selected.email}</a>
                </div>
                {selected.company && (
                  <div className="flex items-center gap-2 text-sm">
                    <Building size={14} className="text-slate-400" />
                    <span className="text-slate-600">{selected.company}</span>
                  </div>
                )}
                {selected.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone size={14} className="text-slate-400" />
                    <span className="text-slate-600">{selected.phone}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm">
                  <Calendar size={14} className="text-slate-400" />
                  <span className="text-slate-400 text-xs">
                    {new Date(selected.created_at).toLocaleString()}
                  </span>
                </div>
              </div>
              <div className="border-t border-slate-100 pt-4">
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">Message</label>
                <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-wrap">{selected.message}</p>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-100 p-12 text-center">
              <Mail size={48} className="text-slate-200 mx-auto mb-4" />
              <p className="text-slate-400">Select a submission to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
